import React, { useState } from "react";
import { CreditCard, Check, Smartphone, ExternalLink, DoorClosed, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { subscriptionApi, billingApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, apiError } from "@/lib/format";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import SeatsCard from "@/pages/settings/SeatsCard";

function planStatus(p) {
  if (!p) return { label: "—", cls: "bg-slate-100 text-slate-600" };
  if (p.source === "store") return { label: "Active subscription", cls: "bg-emerald-100 text-emerald-700" };
  if (p.source === "complimentary") return { label: p.is_trial ? "Trial" : "Complimentary plan", cls: "bg-violet-100 text-violet-700" };
  if (p.locked) return { label: "Free trial ended", cls: "bg-red-100 text-red-700" };
  return { label: "Free trial", cls: "bg-amber-100 text-amber-700" };
}

const EVENT_LABELS = {
  purchase: "Subscription started", renewal: "Renewed", change: "Plan changed",
  lapsed: "Subscription lapsed", trial_started: "Trial started", trial_ended: "Trial ended",
  override_set: "Plan set by FD Check", override_cleared: "Plan override cleared",
  auto_renew_off: "Auto-renew turned off", auto_renew_on: "Auto-renew turned on",
  company_archived: "Company archived", company_restored: "Company restored",
};

export default function SubscriptionTab() {
  const { user } = useAuth();
  const isAdmin = !!(user?.is_company_admin || user?.role === "admin" || user?.is_platform_admin);
  const [busy, setBusy] = useState(null); // lookup_key being processed, or "portal"
  const { data, loading, error, reload } = useAsync(() => subscriptionApi.get(), []);
  const { data: histData } = useAsync(() => subscriptionApi.history(), []);

  async function subscribe(lookupKey) {
    setBusy(lookupKey);
    try {
      const res = await billingApi.checkout({ lookup_key: lookupKey, origin_url: window.location.origin });
      const url = res?.data?.checkout_url || res?.checkout_url;
      if (url) window.location.href = url;
      else { toast.error("Could not start checkout"); setBusy(null); }
    } catch (e) { toast.error(apiError(e)); setBusy(null); }
  }

  async function manageBilling() {
    setBusy("portal");
    try {
      const res = await billingApi.portal({ return_url: window.location.href });
      const url = res?.data?.url || res?.url;
      if (url) window.location.href = url;
      else { toast.error("Could not open billing portal"); setBusy(null); }
    } catch (e) { toast.error(apiError(e)); setBusy(null); }
  }

  if (loading) return <LoadingState label="Loading your subscription…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;

  const p = data?.data || data || {};
  const cat = p.catalogue || {};
  const status = planStatus(p);
  const cap = p.max_doors;
  const used = p.doors_used || 0;
  const pct = cap ? Math.min(100, Math.round((used / cap) * 100)) : null;
  const events = (histData?.data?.events) || (histData?.events) || [];
  const web = p.web_subscription || null;
  const hasWebSub = !!(web && web.active);
  const currentPackage = hasWebSub ? web.package : null;
  const mgmtUrl = p.store_plan?.management_url;
  const tierOrder = cat.tier_order || ["basic", "pro", "pro_maintenance"];
  const tiers = cat.tiers || {};

  return (
    <div className="space-y-6" data-testid="subscription-tab">
      {/* Current plan */}
      <Card className="p-5" data-testid="subscription-current">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <CreditCard className="h-6 w-6 text-slate-700" />
            <div>
              <p className="text-lg font-bold text-slate-900">{p.tier_label || p.tier || "—"}</p>
              <p className="text-sm text-slate-500">{cap == null ? "Unlimited doors" : `${cap} door plan`}</p>
            </div>
          </div>
          <Badge className={status.cls} data-testid="subscription-status">{status.label}</Badge>
        </div>

        {p.days_left != null && (p.free_trial || p.is_trial) && (
          <p className="mt-3 flex items-center gap-1.5 text-sm text-slate-600" data-testid="subscription-days-left">
            <Clock className="h-4 w-4 text-slate-400" />
            {p.locked ? "Your free trial has ended." : `${p.days_left} day${p.days_left === 1 ? "" : "s"} left${p.expires_at ? ` · ends ${fmtDate(p.expires_at)}` : ""}`}
          </p>
        )}

        {/* Door usage */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5 text-slate-600"><DoorClosed className="h-4 w-4 text-slate-400" /> Doors used</span>
            <span className="font-semibold text-slate-800" data-testid="subscription-door-usage">{used}{cap == null ? "" : ` / ${cap}`}</span>
          </div>
          {pct != null && (
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
              <div className={`h-full rounded-full transition-all ${pct >= 100 ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
            </div>
          )}
          {p.at_limit && <p className="mt-1.5 text-xs font-medium text-amber-600">You've reached your door limit — move up a band to add more doors.</p>}
        </div>
      </Card>

      {/* Team seats */}
      <SeatsCard isAdmin={isAdmin} />

      {/* Pay by card on the web */}
      <Card className="border-emerald-100 bg-emerald-50/50 p-5" data-testid="subscription-how">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <CreditCard className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
            <div className="min-w-0">
              <p className="font-semibold text-slate-800">Subscribe by card, right here</p>
              <p className="mt-1 text-sm text-slate-600">Pick a plan below and pay securely by card. Your subscription renews yearly and upgrades your whole team instantly. You can change or cancel anytime.</p>
              <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500"><Smartphone className="h-3.5 w-3.5" /> Prefer the app? You can also subscribe via the App Store / Google Play in the FD Check mobile app.</p>
            </div>
          </div>
          {hasWebSub && isAdmin && (
            <Button variant="outline" size="sm" disabled={busy === "portal"} onClick={manageBilling} data-testid="subscription-manage-billing">
              {busy === "portal" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ExternalLink className="mr-1.5 h-4 w-4" />} Manage billing
            </Button>
          )}
        </div>
        {!hasWebSub && mgmtUrl && (
          <a href={mgmtUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid="subscription-manage-link">
            <ExternalLink className="h-3.5 w-3.5" /> Manage your current subscription
          </a>
        )}
      </Card>

      {/* Plan catalogue */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {tierOrder.map((key) => {
          const t = tiers[key];
          if (!t) return null;
          const current = key === p.tier && p.source !== "free";
          return (
            <Card key={key} className={`flex flex-col p-5 ${current ? "border-primary ring-1 ring-primary/30" : ""}`} data-testid={`subscription-plan-${key}`}>
              <div className="flex items-center justify-between">
                <p className="text-lg font-bold text-slate-900">{t.label}</p>
                {current && <Badge className="bg-primary/10 text-primary">Your plan</Badge>}
              </div>
              {t.tagline && <p className="mt-0.5 text-sm text-slate-500">{t.tagline}</p>}
              {Array.isArray(t.bands) && (
                <div className="mt-3 space-y-2">
                  {t.bands.map((b) => {
                    const isCurrentBand = currentPackage === b.package;
                    return (
                      <div key={b.package} className="flex items-center justify-between gap-2 text-sm">
                        <span className="text-slate-600">Up to {b.doors} doors</span>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-800">{b.price || "—"}/yr</span>
                          {isAdmin && (
                            isCurrentBand ? (
                              <Badge className="bg-emerald-100 text-emerald-700" data-testid={`subscription-current-band-${b.package}`}>Current</Badge>
                            ) : (
                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={busy === b.package} onClick={() => subscribe(b.package)} data-testid={`subscribe-${b.package}`}>
                                {busy === b.package ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (hasWebSub ? "Switch" : "Subscribe")}
                              </Button>
                            )
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {t.blurb && <p className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-400">{t.blurb}</p>}
            </Card>
          );
        })}
      </div>

      {/* Billing history */}
      <Card className="p-5" data-testid="subscription-history">
        <p className="mb-3 font-semibold text-slate-800">Billing history</p>
        {events.length === 0 ? (
          <EmptyState icon={CreditCard} title="No billing activity yet" message="Your subscription events will appear here." />
        ) : (
          <div className="divide-y divide-slate-50">
            {events.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-3 py-2.5 text-sm" data-testid={`subscription-event-${e.id}`}>
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 font-medium text-slate-800"><Check className="h-3.5 w-3.5 text-emerald-500" />{EVENT_LABELS[e.event] || e.event}</p>
                  <p className="truncate text-xs text-slate-500">{[e.tier, e.price_string, e.note].filter(Boolean).join(" · ") || "—"}</p>
                </div>
                <span className="shrink-0 text-xs text-slate-400">{fmtDate(e.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
